import { useState, useEffect, useRef } from 'react';
import { Target, Calendar, TrendingUp, DollarSign, X, Settings } from 'lucide-react';
import { TargetType } from '@/lib/calculator';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import * as Dialog from '@radix-ui/react-dialog';
import ReactDOM from 'react-dom';

interface TargetSelectorProps {
  selected: TargetType;
  onSelect: (target: TargetType) => void; // This is the handler we're using now
  onOpenEditInputs: () => void;
}

// Target options with their metadata
const targets = [
  {
    id: 'price' as const,
    label: 'Maximum Price',
    icon: Target,
    description: 'Find your ideal budget range',
    emoji: '🎯'
  },
  {
    id: 'payment' as const,
    label: 'Mortgage Payment',
    icon: DollarSign,
    description: 'Calculate monthly costs',
    emoji: '💵'
  },
  {
    id: 'date' as const,
    label: 'Purchase Date',
    icon: Calendar,
    description: 'Plan your timeline',
    emoji: '📅'
  },
  {
    id: 'savings' as const,
    label: 'Required Savings',
    icon: TrendingUp,
    description: 'Set saving goals',
    emoji: '📈'
  }
];

export function TargetSelector({ selected, onSelect, onOpenEditInputs }: TargetSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const savedScrollPosition = useRef<number | null>(null);
  
  // Find currently selected target metadata
  const selectedTarget = targets.find(t => t.id === selected) || targets[0];
  
  // When the modal opens, save current scroll position and scroll to the button
  useEffect(() => {
    if (isOpen && containerRef.current) {
      // Save current scroll position
      savedScrollPosition.current = window.scrollY;
      
      // Scroll to the target selector button with a small offset for better visibility
      const rect = containerRef.current.getBoundingClientRect();
      const targetY = window.scrollY + rect.top - 20; // 20px offset above the element
      
      // Smooth scroll to the target selector
      window.scrollTo({
        top: targetY,
        behavior: 'smooth'
      });
      
      // Also disable body scroll to prevent background scrolling while modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      if (savedScrollPosition.current !== null && !isOpen) {
        // Re-enable scrolling
        document.body.style.overflow = '';
        
        // No need to restore scroll position - keep the user where they scrolled to
      }
    };
  }, [isOpen]);
  
  const handleSelect = (targetId: TargetType) => {
    onSelect(targetId); // Use the parent component's handler
    setIsOpen(false);
  };

  // Modal content that will be portaled to body
  const ModalContent = () => {
    if (!isOpen) return null;
    
    return ReactDOM.createPortal(
      <>
        {/* Overlay */}
        <Dialog.Overlay 
          className="fixed inset-0 bg-black/50 z-50" 
          onClick={() => setIsOpen(false)}
        />
        
        {/* Modal Content */}
        <Dialog.Content 
          className="fixed left-[50%] top-[50%] z-50 w-full max-w-md max-h-[85vh] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-xl overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              Select Calculation Objective
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>
          
          <div className="space-y-2">
            {targets.map((target) => {
              const isSelected = selected === target.id;
              
              return (
                <div key={target.id} className="relative">
                  <button
                    className={cn(
                      "w-full flex items-center gap-3 p-4 rounded-lg text-left hover:bg-gray-50 transition-colors relative overflow-hidden",
                      isSelected && "border-2 border-[#1A5173]" // Updated color to match new theme
                    )}
                    onClick={() => handleSelect(target.id)}
                  >
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1A5173]/10 text-xl flex-shrink-0">
                      {target.emoji}
                    </div>
                    <div>
                      <div className="font-medium text-[#1E2A36]">{target.label}</div>
                      <div className="text-sm text-[#566577]">{target.description}</div>
                    </div>
                    
                    {isSelected && (
                      <motion.div
                        layoutId="selected-indicator"
                        className="absolute right-3 w-5 h-5 bg-[#1A5173] rounded-full flex items-center justify-center"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </motion.div>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </Dialog.Content>
      </>,
      document.body
    );
  };

  return (
    <div ref={containerRef} className="p-3 sm:p-5 bg-white rounded-lg border border-[#D9DFE6] mb-2 shadow-sm">
      <h2 className="text-lg font-semibold text-[#1E2A36] mb-2">Objective</h2>
      
      <div className="flex flex-col sm:flex-row gap-2">
        {/* Target Selector Button - Updated gradient to match new theme */}
        <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
          <Dialog.Trigger asChild>
            <button 
              ref={buttonRef}
              className="flex-1 flex items-center px-4 py-3 rounded-lg hover:bg-[#0A324A] transition-colors" 
              style={{ 
                background: "linear-gradient(135deg, #0A324A 0%, #1A5173 100%)"
              }}
            >
              <div className="flex items-center">
                <span className="text-xl mr-3">{selectedTarget.emoji}</span>
                <div className="flex flex-col items-start">
                  <span className="font-medium text-white text-base">{selectedTarget.label}</span>
                  <span className="text-xs text-blue-100">{selectedTarget.description}</span>
                </div>
              </div>
            </button>
          </Dialog.Trigger>
          
          {/* Portal modal content */}
          <ModalContent />
        </Dialog.Root>
        
        {/* Edit Inputs Button - Now using the new primary color */}
        <button
          onClick={onOpenEditInputs}
          className="flex-1 sm:flex-none sm:w-40 flex items-center justify-center gap-2 px-4 py-3 bg-[#1A5173] text-white rounded-lg hover:bg-[#0A324A] transition-colors"
        >
          <Settings className="w-5 h-5 text-white" />
          <span>Edit Inputs</span>
        </button>
      </div>
    </div>
  );
}